<script lang="ts" setup>
import {onMounted, ref} from "vue";
import ModalComponent from "@/components/ModalComponent.vue";
import BtnCta from "@/components/buttons/BtnCta.vue";
import Btn from "@/components/buttons/Btn.vue";
import Dropdown from "@/components/Dropdown.vue";
import BaseAnnouncementBar from "@/components/BaseAnnouncementBar.vue";
import TheAnnouncementBar from "@/components/TheAnnouncementBar.vue";
import type {Announcement} from "@/features/announcements/types";
import BaseNotificationPopup from "@/components/BaseNotificationPopup.vue";
import AuthVerifyModal from "@/features/auth-verify/AuthVerifyModal.vue";

import IconPencil from '~icons/tabler/pencil';
import IconTrash from '~icons/tabler/trash';
import IconUser from '~icons/tabler/user';
import IconCompany from '~icons/tabler/building';
import IconHouse from '~icons/tabler/home';
import IconDimensions from '~icons/tabler/cube';
import IconSearch from '~icons/tabler/search';
import IconGroup from '~icons/tabler/users';
import IconBell from '~icons/tabler/bell';
import IconDashboard from '~icons/tabler/layout-dashboard';
import IconSalaries from '~icons/tabler/cash';
import IconBuy from '~icons/tabler/shopping-cart';
import IconVacation from '~icons/tabler/beach';
import IconAbsence from '~icons/tabler/user-off';
import IconSickLeave from '~icons/tabler/medical-cross';
import IconWithholdings from '~icons/tabler/cash-off';
import IconLock from '~icons/tabler/lock';
import IconPayouts from '~icons/tabler/cash-banknote';
import IconCalendar from '~icons/tabler/calendar';
import IconCalculator from '~icons/tabler/calculator';
import IconSettings from '~icons/tabler/settings';
import IconAdd from '~icons/tabler/plus';
import IconAttachment from '~icons/tabler/paperclip';
import IconComment from '~icons/tabler/message';
import IconEmail from '~icons/tabler/mail';
import IconWarning from '~icons/tabler/alert-triangle';
import IconLogOut from '~icons/tabler/logout';
import IconHelp from '~icons/tabler/help-circle';
import IconLightBulb from '~icons/tabler/bulb';
import IconThermometer from '~icons/tabler/thermometer';
import IconDetails from '~icons/tabler/info-circle';
import IconList from '~icons/tabler/list';
import IconDotsVertical from '~icons/tabler/dots-vertical';
import IconShowAll from '~icons/tabler/eye';
import IconUserGear from '~icons/tabler/user-cog';
import IconContract from '~icons/tabler/file';
import IconLicense from '~icons/tabler/license';
import IconSquareCheck from '~icons/tabler/square-check';

defineOptions({ name: "DemoAdmin" });

const showModal = ref(false);
const showVerifyModal = ref(false);
const showErrorBar = ref(true);
const showSuccessBar = ref(true);
const demoAnnouncements: Announcement[] = [
  {
    id: '11111111-1111-1111-1111-111111111111',
    severity: 'error',
    state: 'settled',
    visible: true,
    translationKey: 'lbl-tsd-returned-with-errors',
    parameters: null,
    linkTarget: 'tsd-report',
    linkTextKey: 'lbl-tsd-fix-transactions',
    linkAction: 'show-errors',
    expiresAt: null,
    checkEverySeconds: null,
  },
  {
    id: '22222222-2222-2222-2222-222222222222',
    severity: 'success',
    state: 'settled',
    visible: true,
    translationKey: 'lbl-tsd-accepted',
    parameters: null,
    linkTarget: null,
    linkTextKey: null,
    linkAction: null,
    expiresAt: null,
    checkEverySeconds: null,
  },
  {
    id: '33333333-3333-3333-3333-333333333333',
    severity: 'info',
    state: 'pending',
    visible: true,
    translationKey: 'lbl-tsd-sent-awaiting-emta',
    parameters: null,
    linkTarget: null,
    linkTextKey: null,
    linkAction: null,
    // A pending announcement carries an expiry so the client stops waiting if no outcome comes.
    expiresAt: '2099-01-01T00:00:00Z',
    checkEverySeconds: null,
  },
];
const lastVerifyToken = ref<string | null>(null);
const verifyTranslations = {
  'lbl-auth-verify-deletecompany-title': 'Confirm with your eID',
  'lbl-ident-by-mobile-id': 'Mobile-ID',
  'lbl-ident-by-smart-id': 'Smart-ID',
  'lbl-ident-by-id-card': 'ID-card',
  'btn-start': 'Start',
  'btn-cancel': 'Cancel',
  'btn-close': 'Close'
};

function onVerified(token: string | null) {
  lastVerifyToken.value = token ?? '(no token — JWT-gated purpose)';
  showVerifyModal.value = false;
}

const currencyOptions = [
  { value: "EUR", label: "EUR — Euro" },
  { value: "USD", label: "USD — US Dollar" },
  { value: "GBP", label: "GBP — British Pound" },
  { value: "JPY", label: "JPY — Japanese Yen" },
  { value: "SEK", label: "SEK — Swedish Krona" },
] as const;

type CurrencyCode = (typeof currencyOptions)[number]["value"];

const currencyM = ref<CurrencyCode>("EUR");
const currencyS = ref<CurrencyCode>("EUR");
const dropdownsDisabled = ref(false);

onMounted(() => {
  document.title = "Admin/Demo";
});
</script>

<template>
  <div>
    <h2>Demo Area</h2>
    <p>This area is available for testing new components.</p>

    <section class="icon-gallery">
      <h3>Random icons (unplugin-icons · Tabler · stroke-width 1.5)</h3>
      <ul class="icon-row">
        <li><IconPencil /><span>edit</span><code>tabler/pencil</code></li>
        <li><IconTrash /><span>delete</span><code>tabler/trash</code></li>
        <li><IconUser /><span>user</span><code>tabler/user</code></li>
        <li><IconCompany /><span>company</span><code>tabler/building</code></li>
        <li><IconHouse /><span>house</span><code>tabler/home</code></li>
        <li><IconDimensions /><span>dimensions</span><code>tabler/cube</code></li>
        <li><IconSearch /><span>search</span><code>tabler/search</code></li>
        <li><IconGroup /><span>group</span><code>tabler/users</code></li>
        <li><IconBell /><span>notification</span><code>tabler/bell</code></li>
        <li><IconDashboard /><span>dashboard</span><code>tabler/layout-dashboard</code></li>
        <li><IconSalaries /><span>salaries</span><code>tabler/cash</code></li>
        <li><IconBuy /><span>buy</span><code>tabler/shopping-cart</code></li>
        <li><IconVacation /><span>vacation</span><code>tabler/beach</code></li>
        <li><IconAbsence /><span>absence</span><code>tabler/user-off</code></li>
        <li><IconSickLeave /><span>sick leave</span><code>tabler/medical-cross</code></li>
        <li><IconWithholdings /><span>withholdings</span><code>tabler/cash-off</code></li>
        <li><IconLock /><span>lock</span><code>tabler/lock</code></li>
        <li><IconPayouts /><span>payouts</span><code>tabler/cash-banknote</code></li>
        <li><IconCalendar /><span>calendar</span><code>tabler/calendar</code></li>
        <li><IconCalculator /><span>calculator</span><code>tabler/calculator</code></li>
        <li><IconSettings /><span>settings</span><code>tabler/settings</code></li>
        <li><IconAdd /><span>add</span><code>tabler/plus</code></li>
        <li><IconAttachment /><span>attachment</span><code>tabler/paperclip</code></li>
        <li><IconComment /><span>comment</span><code>tabler/message</code></li>
        <li><IconEmail /><span>email</span><code>tabler/mail</code></li>
        <li><IconWarning /><span>warning</span><code>tabler/alert-triangle</code></li>
        <li><IconLogOut /><span>log out</span><code>tabler/logout</code></li>
        <li><IconHelp /><span>help</span><code>tabler/help-circle</code></li>
        <li><IconLightBulb /><span>tip</span><code>tabler/bulb</code></li>
        <li><IconThermometer /><span>thermometer</span><code>tabler/thermometer</code></li>
        <li><IconDetails /><span>details</span><code>tabler/info-circle</code></li>
        <li><IconList /><span>list</span><code>tabler/list</code></li>
        <li><IconDotsVertical /><span>more</span><code>tabler/dots-vertical</code></li>
        <li><IconShowAll /><span>show all</span><code>tabler/eye</code></li>
        <li><IconUserGear /><span>user settings</span><code>tabler/user-cog</code></li>
        <li><IconContract /><span>contract</span><code>tabler/file</code></li>
        <li><IconLicense /><span>license</span><code>tabler/license</code></li>
      </ul>
    </section>

    <section class="stroke-demo">
      <h3>Tabler stroke-width</h3>
      <p class="stroke-demo__hint">Same Tabler icon (<code>square-check</code>) at three stroke widths — line weight is a CSS knob, no separate icon files needed.</p>
      <ul class="stroke-demo__row">
        <li class="stroke-demo__cell">
          <IconSquareCheck class="stroke-icon stroke-icon--sw11"/>
          <span>stroke-width: 1.1</span>
        </li>
        <li class="stroke-demo__cell">
          <IconSquareCheck class="stroke-icon stroke-icon--sw15"/>
          <span>stroke-width: 1.5</span>
        </li>
        <li class="stroke-demo__cell">
          <IconSquareCheck class="stroke-icon stroke-icon--sw20"/>
          <span>stroke-width: 2 (default)</span>
        </li>
      </ul>
    </section>

    <section class="dropdown-demo">
      <h3>Dropdown</h3>
      <p class="dropdown-demo__hint">Reusable <code>&lt;Dropdown&gt;</code> from <code>@/components/Dropdown.vue</code>. Same component, two sizes — <code>m</code> is the form-field default, <code>s</code> is the compact navigational picker.</p>
      <label class="dropdown-demo__toggle">
        <input v-model="dropdownsDisabled" type="checkbox">
        Disable both dropdowns
      </label>
      <div class="dropdown-demo__row">
        <div class="dropdown-demo__cell">
          <label class="dropdown-demo__label">Size m (default)</label>
          <Dropdown
            v-model="currencyM"
            :options="currencyOptions"
            size="m"
            :disabled="dropdownsDisabled"
            aria-label="Currency (m)"
          />
          <div class="dropdown-demo__value">value: <code>{{ currencyM }}</code></div>
        </div>
        <div class="dropdown-demo__cell">
          <label class="dropdown-demo__label">Size s</label>
          <Dropdown
            v-model="currencyS"
            :options="currencyOptions"
            size="s"
            :disabled="dropdownsDisabled"
            aria-label="Currency (s)"
          />
          <div class="dropdown-demo__value">value: <code>{{ currencyS }}</code></div>
        </div>
      </div>
    </section>

    <section class="announcement-demo">
      <h3>BaseAnnouncementBar</h3>
      <p class="announcement-demo__hint">Full-bleed page notice from <code>@/components/BaseAnnouncementBar.vue</code>. Three variants; the message is a slot, so a caller can pass an inline link and it picks up the design's link treatment via <code>:slotted</code>.</p>
      <BaseAnnouncementBar v-if="showErrorBar" variant="error" @dismiss="showErrorBar = false">
        TSD aruanne on saadetud tagasi vigade ja hoiatustega. <a href="#">Näita tehinguid</a>
      </BaseAnnouncementBar>
      <BaseAnnouncementBar v-if="showSuccessBar" variant="success" @dismiss="showSuccessBar = false">
        TSD aruanne on edukalt saadetud.
      </BaseAnnouncementBar>
      <BaseAnnouncementBar variant="info" :dismissible="false">
        Maksu- ja Tolliamet ei võta praegu tehinguid vastu. Proovi hiljem uuesti.
      </BaseAnnouncementBar>
      <BaseAnnouncementBar variant="success" :dismissible="false">
        Not dismissible — no close button.
      </BaseAnnouncementBar>
      <Btn @click="showErrorBar = true; showSuccessBar = true">Reset bars</Btn>
    </section>

    <section class="announcement-demo">
      <h3>TheAnnouncementBar</h3>
      <p class="announcement-demo__hint">
        The program-wide host (<code>@/components/TheAnnouncementBar.vue</code>, MERP-2058), mounted once above the
        main menu in <code>MainLayout.cshtml</code> and fed the mailbox as <code>data-props</code>. It renders a stack of
        <code>BaseAnnouncementBar</code>, resolves <code>translationKey</code> through the announcements i18n bundle, and
        makes the announcement clickable when it carries a <code>linkTarget</code> the navigator can reach —
        outside the main program there is no menu to reach, so the demo rows render as plain text. Keys with no DB row yet render as
        their own name — that is the designed fallback, not a bug.
      </p>
      <TheAnnouncementBar :company-id="0" :announcements="demoAnnouncements" />
    </section>

    <section class="popup-demo">
      <h3>BaseNotificationPopup</h3>
      <p class="popup-demo__hint">Outcome card from <code>@/components/BaseNotificationPopup.vue</code>. Three variants; the body is a slot and the action button only renders when <code>actionLabel</code> is set. Icons are Tabler — the Figma layer is literally named <code>TablerCircleCheck</code>. The loading variant is the Chase loader the design annotates (#2EB336).</p>
      <div class="popup-demo__row">
        <BaseNotificationPopup variant="loading" title="Tehingud on saatmisel">
          Saatmine võib võtta aega. Palun oodake.
        </BaseNotificationPopup>
        <BaseNotificationPopup variant="success" title="Tehingute saatmine õnnestus" action-label="Sulge">
          Tehingud on edukalt saadetud Maksu- ja Tolliametile.
        </BaseNotificationPopup>
        <BaseNotificationPopup variant="error" title="Tehingute saatmine ebaõnnestus" action-label="Sulge">
          Väljamaksetes esineb vigu või hoiatusi.
        </BaseNotificationPopup>
      </div>
    </section>

    <section class="modal-demo">
      <h3>Modal demo</h3>
      <BtnCta @click="showModal = true">Open modal</BtnCta>
    </section>

    <ModalComponent :show="showModal" @close="showModal = false">
      <template #header>
        <h2>Hello from the demo modal</h2>
      </template>
      <p>Close button in the header uses <code>IconBtnClose</code> with <code>ic:baseline-close</code>.</p>
      <p>Hover the X to see the red flip.</p>
      <template #footer>
        <Btn @click="showModal = false">Cancel</Btn>
        <BtnCta @click="showModal = false">OK</BtnCta>
      </template>
    </ModalComponent>

    <section class="auth-verify-demo">
      <h3>Auth-verify modal demo</h3>
      <p>Mounts <code>AuthVerifyModal</code> with <code>purpose=demo</code>, <code>resourceId=demo-1</code>. Runs against the real <code>/api/app/auth/verify/*</code> endpoints — a successful ceremony will mint a token but no destructive action consumes it.</p>
      <BtnCta @click="showVerifyModal = true">Open verify modal</BtnCta>
      <p v-if="lastVerifyToken" class="auth-verify-demo__token">Last minted token: <code>{{ lastVerifyToken }}</code></p>
    </section>

    <AuthVerifyModal
      v-if="showVerifyModal"
      :translations="verifyTranslations"
      purpose="demo"
      resource-id="demo-1"
      title-key="lbl-auth-verify-deletecompany-title"
      @verified="onVerified"
      @close="showVerifyModal = false"
    />
  </div>
</template>

<style scoped>
.icon-gallery {
  margin-top: 24px;
}

.icon-row {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0;
  margin: 12px 0 0;
}

.icon-row li {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 110px;
  padding: 12px;
  font-size: 11px;
  color: #555;
}

.icon-row svg {
  width: 28px;
  height: 28px;
  color: #919191;
}

.stroke-demo {
  margin-top: 32px;
}

.stroke-demo__hint {
  font-size: 13px;
  color: #555;
  margin: 6px 0 12px;
}

.stroke-demo__row {
  list-style: none;
  display: flex;
  gap: 32px;
  padding: 0;
  margin: 0;
}

.stroke-demo__cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #555;
}

.stroke-icon {
  width: 64px;
  height: 64px;
  color: #2EB336;
}

/* Stroke-width overrides live in the unscoped block below. Tabler icons set
   `stroke-width="2"` as a presentation attribute on an inner <g>, not on the
   <svg> root — a CSS rule on the SVG can't override it because the attribute
   is a declared value on the <g> itself (inheritance only fills unset values).
   We have to match the inner elements directly. */

.dropdown-demo {
  margin-top: 32px;
}

.dropdown-demo__hint {
  font-size: 13px;
  color: #555;
  margin: 6px 0 12px;
}

.dropdown-demo__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #333;
  margin: 0 0 16px;
  cursor: pointer;
}

.dropdown-demo__row {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
}

/* Grey backdrop so the white popup cards read as cards against the demo page. */
.popup-demo__row {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
  padding: 24px;
  background: #F4F5F9;
}

.dropdown-demo__cell {
  width: 220px;
}

.dropdown-demo__label {
  display: block;
  font-size: 12px;
  color: #555;
  margin-bottom: 6px;
}

.dropdown-demo__value {
  margin-top: 8px;
  font-size: 11px;
  color: #777;
}

.modal-demo {
  margin-top: 32px;
}

.icon-row li code {
  font-size: 10px;
  color: #888;
  background: #f4f5f9;
  padding: 1px 4px;
  border-radius: 2px;
}
</style>

<style>
.icon-row svg :is(g, path) { stroke-width: 1.5; }

.stroke-icon--sw11 :is(g, path) { stroke-width: 1.1; }
.stroke-icon--sw15 :is(g, path) { stroke-width: 1.5; }
.stroke-icon--sw20 :is(g, path) { stroke-width: 2; }
</style>
